import { Injectable } from '@angular/core';
import { colorLuminance } from '@helpers/color-luminance';
import { ResultsetItem } from '@models/resultset-item';
import { ScenarioColorItem } from '@models/scenario-color-item';
import { getUserLocales } from 'get-user-locale';
import * as GC from '@grapecity/spread-sheets';
import { BASELINE_COLORS, BASELINE_COLORS_ALTERNATIVE, STRESS_COLORS, STRESS_COLORS_ALTERNATIVE } from '../results/results-colors';

function getValidLocale() {
  const locales: string[] = getUserLocales();
  // Find the first xx-XX locale or default to en-US if not found
  const validLocale = locales.find(loc => /[a-z]{2}-[A-Z]{2}/.test(loc)) || 'en-US';
  return validLocale;
}

@Injectable({ providedIn: 'root' })
export class SettingsService {
  static locale = getValidLocale();
  private user: any;
  private app: any;
  private layout: any;
  public resultsColors: ScenarioColorItem[] = [];

  constructor() {
    // User Settings
    // -----------------------------------
    this.user = {
      name: 'John',
      job: 'ng-developer',
      picture: 'assets/img/user/02.jpg',
    };

    // App Settings
    // -----------------------------------
    this.app = {
      name: 'Empyrean-Solutions',
      description: 'Empyrean-Solutions Liquidity Stress Test',
      year: new Date().getFullYear(),
      adminEmail: 'administrator@admin.com',
    };

    // Layout Settings
    // -----------------------------------
    this.layout = {
      isFixed: true,
      isCollapsed: false,
      isBoxed: false,
      isRTL: false,
      horizontal: false,
      isFloat: false,
      asideHover: false,
      theme: null,
      asideScrollbar: false,
      isCollapsedText: false,
      useFullLayout: false,
      hiddenFooter: false,
      offsidebarOpen: false,
      asideToggled: false,
      viewAnimation: 'ng-fadeInUp',
    };
  }

  getAppSetting(name) {
    return name ? this.app[name] : this.app;
  }
  getUserSetting(name) {
    return name ? this.user[name] : this.user;
  }
  getLayoutSetting(name) {
    return name ? this.layout[name] : this.layout;
  }

  setAppSetting(name, value) {
    if (typeof this.app[name] !== 'undefined') {
      this.app[name] = value;
    }
  }
  setUserSetting(name, value) {
    if (typeof this.user[name] !== 'undefined') {
      this.user[name] = value;
    }
  }
  setLayoutSetting(name, value) {
    if (typeof this.layout[name] !== 'undefined') {
      return (this.layout[name] = value);
    }
  }

  toggleLayoutSetting(name) {
    return this.setLayoutSetting(name, !this.getLayoutSetting(name));
  }

  /**
   * It sets a color for every result based on his scenario
   * Considerations:
   * ===============
   * - Baselines should have a different shades of BLUE while Stresses should have NON-BLUE color shades
   * - Every resultset with the same scenario should have the same color
   */
  public setResultsColors(resultsList: ResultsetItem[]): void {
    if (resultsList.length > 0) {
      resultsList.forEach((result: ResultsetItem) => {
        const colorAssigned = this.resultsColors.find(item => item.Scenario_ID === result.Scenario_ID) || false;
        if (!colorAssigned) {
          const colorItem = <ScenarioColorItem>{
            Scenario_ID: result.Scenario_ID || (result['TheScenario'] && result['TheScenario'].ScenarioID),
            sheetNames: [result.Mnemonic],
            color: this.generateScenarioColor(result),
          };
          if (colorItem.Scenario_ID) {
            this.resultsColors.push(colorItem);
          }
        } else if (colorAssigned && !colorAssigned.sheetNames.includes(result.Mnemonic)) {
          colorAssigned.sheetNames.push(result.Mnemonic);
        }
      });

      // console.log('Colors:', this.resultsColors);
    } else {
      // Color's Reset
      this.resultsColors = [];
    }
  }

  public setSheetColor(workSheet: GC.Spread.Sheets.Worksheet) {
    const sheetName = workSheet.name().replace('-summary', ''); // Both: summary & detail
    const cItem = sheetName && this.resultsColors.find((item: ScenarioColorItem) => item.sheetNames.includes(sheetName));
    const scenColor = cItem && cItem.color;
    if (scenColor) {
      workSheet.options.sheetTabColor = scenColor;
    }
  }

  /**
   * Generates a color based on a colorPalette and the Scenario_ID.
   * Changes in the colorPalette could alter the colors generated for each item.
   * For the generation of colors we are following the next rules:
   * 1) generates a color based on Scenario_ID, using a color from a primary color palette
   * 2) If the color candidate would be repeated, it uses the first available color in the primary color palette
   * 2) If there is no available colors, it uses the first available color in the secondary color palette
   * 5) If all colors from both palettes are being used, it generates a similar shape of the color candidate
   */
  private generateScenarioColor(resultset: ResultsetItem) {
    const keyName = 'Scenario_ID';
    const colorPalette: string[] = resultset.Is_Stress ? STRESS_COLORS : BASELINE_COLORS;
    const colorPalette_Alternative: string[] = resultset.Is_Stress ? STRESS_COLORS_ALTERNATIVE : BASELINE_COLORS_ALTERNATIVE;

    const genColor = (colorIndex, maxIndex, palette) => {
      if (colorIndex > maxIndex) {
        colorIndex = Math.max(0, Math.floor(colorIndex % maxIndex));
      }
      const colorCandidate = (colorIndex > -1 && colorPalette[colorIndex]) || null;

      // Avoid repetition of colors
      const isRepeatedCheck = (testColor: string) => this.resultsColors.map(cItem => cItem.color).includes(testColor);
      const getUnAssignedColor = () =>
        palette.find(color => !isRepeatedCheck(color)) ||
        colorPalette_Alternative.find(color => !isRepeatedCheck(color)) ||
        // All colorPalette is being used so generate alternative shapes of the candidate color
        colorLuminance(colorCandidate, -0.15) ||
        null;
      return isRepeatedCheck(colorCandidate) ? getUnAssignedColor() : colorCandidate;
    };

    if (colorPalette.length > 0 && colorPalette && colorPalette.length > 0) {
      const maxIndex: number = colorPalette.length - 1;
      if (maxIndex > -1) {
        const colorIndex: number = resultset[keyName] > 0 ? resultset[keyName] : 0;
        return genColor(colorIndex, maxIndex, colorPalette);
      }
    }
    return null;
  }

  public getScenarioColor(resultItem: ResultsetItem, sheetName = null) {
    const colorItem: ScenarioColorItem = this.getResultColorItem(resultItem, sheetName);
    return colorItem ? colorItem.color : '#cccccc';
  }

  private getResultColorItem(resultset: ResultsetItem, sheetName = null): ScenarioColorItem {
    const findColorCriteria = (colorItem: ScenarioColorItem) =>
      sheetName ? colorItem.sheetNames.indexOf(sheetName) > -1 : colorItem.Scenario_ID === resultset.Scenario_ID;

    // Helper to retrieve it If has been generated already
    const itemFound: ScenarioColorItem = this.resultsColors.find(findColorCriteria);

    // If we cannot find it, then generate it
    if (!itemFound) {
      this.setResultsColors([resultset as ResultsetItem]);
      // After being generated, find it
      return this.resultsColors.find(findColorCriteria);
    }

    return itemFound;
  }
}
